import { useEffect, useState, useCallback, useRef } from "react";
import { Avatar } from "@chakra-ui/avatar";
import {
  Box,
  Text,
  FormControl,
  // Input,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { AiOutlineSend } from "react-icons/ai";
import { BsFillMicFill } from "react-icons/bs";
import socket from "../socket";

import { ChatState } from "../Context/ChatProvider";
import axios from "../axiosConfig";
import { getSender, getSenderFull } from "../config/ChatLogics";
import ScrollableChat from "./ScrollableChat";
import ProfileModal from "./miscellaneous/ProfileModal";
import UpdateGroupChatModal from "./miscellaneous/UpdateGroupChatModal";
import { reorderChats } from "../utils/chatHelpers";
import { Textarea } from "@chakra-ui/react";

const SingleChat = ({ fetchAgain, setFetchAgain }) => {
  const { messages, setMessages } = ChatState();
  const [newMessage, setNewMessage] = useState("");
  const [typing, setTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [sending, setSending] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [userStatusInfo, setUserStatusInfo] = useState({
    status: "",
    lastSeen: null,
  });

  const toast = useToast();
  const { selectedChat, setSelectedChat, user, setChats } = ChatState();
  const lastTypingTimeRef = useRef(null);

  useEffect(() => {
    const storedChat = localStorage.getItem("selectedChat");
    if (storedChat) {
      const parsedChat = JSON.parse(storedChat);
      if (parsedChat.users.some((u) => u._id === user._id)) {
        setSelectedChat(parsedChat);
      } else {
        localStorage.removeItem("selectedChat");
      }
    }
  }, [setSelectedChat, user._id]);

  const fetchMessages = useCallback(async () => {
    if (!selectedChat) return;
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      const { data } = await axios.get(
        `/api/message/${selectedChat._id}`,
        config
      );
      const normalized = data.map((msg) => ({
        ...msg,
        sender:
          typeof msg.sender === "object"
            ? msg.sender
            : { _id: msg.sender, name: "Unknown", pic: "" },
      }));
      setMessages(normalized);
      await axios.post(`/api/message/seen/${selectedChat._id}`, {}, config);
    } catch (error) {
      toast({
        title: "Error Occurred!",
        description: "Failed to Load the Messages",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  }, [selectedChat, user.token, toast, setMessages]);

  const fetchUserStatus = useCallback(async () => {
    const targetUser = getSenderFull(user, selectedChat?.users)?._id;
    if (!targetUser) return;

    try {
      const { data } = await axios.get(`/api/user/status/${targetUser}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setUserStatusInfo(data);
    } catch (error) {
      console.error("Failed to fetch user status:", error.message);
    }
  }, [selectedChat, user]);

  const sendMessage = useCallback(async () => {
    if (!newMessage.trim()) return;
    socket.emit("stop typing", {
      chatId: selectedChat._id,
      username: user.name,
    });
    setSending(true);
    try {
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      };
      const { data } = await axios.post(
        "/api/message",
        {
          content: newMessage,
          chatId: selectedChat._id,
        },
        config
      );

      const newMsg = {
        ...data,
        sender: {
          _id: user._id,
          name: user.name,
          pic: user.pic,
        },
        chat: selectedChat,
      };

      setNewMessage("");
      setMessages((prev) => [...prev, newMsg]);
      socket.emit("new message", newMsg);
      setChats((prevChats) => reorderChats(prevChats, newMsg));
      await fetchMessages();
    } catch (error) {
      toast({
        title: "Error Occurred!",
        description: "Failed to send the Message",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setSending(false);
    }
  }, [
    newMessage,
    selectedChat,
    user,
    toast,
    setChats,
    fetchMessages,
    setMessages,
  ]);

  const typingHandler = (e) => {
    setNewMessage(e.target.value);
    if (!socketConnected) return;

    if (!typing) {
      setTyping(true);
      socket.emit("typing", { chatId: selectedChat._id, username: user.name });
    }

    lastTypingTimeRef.current = new Date().getTime();

    setTimeout(() => {
      const now = new Date().getTime();
      const diff = now - lastTypingTimeRef.current;
      if (diff >= 3000 && typing) {
        socket.emit("stop typing", {
          chatId: selectedChat._id,
          username: user.name,
        });
        setTyping(false);
      }
    }, 3000);
  };

  const startSpeechToText = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast({
        title: "Browser not supported",
        description: "Speech recognition is not supported in this browser.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsRecording(true);
      toast({
        title: "Listening...",
        description: "Start speaking now.",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((res) => res[0])
        .map((res) => res.transcript)
        .join("");
      setNewMessage(transcript);
    };

    recognition.onerror = (event) => {
      toast({
        title: "Error Occurred!",
        description: event.error || "Speech recognition error.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    };

    recognition.onend = () => setIsRecording(false);
    recognition.start();
  };

  const handleDelete = async (messageId, type) => {
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      await axios.delete(
        `/api/message/delete/${messageId}?type=${type}`,
        config
      );
      setMessages((prev) => prev.filter((msg) => msg._id !== messageId));
      if (type === "everyone") {
        socket.emit("message deleted", { messageId, chatId: selectedChat._id });
      }
    } catch (err) {
      toast({
        title: "Delete failed",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (!user || !selectedChat) return;
    if (!selectedChat.isGroupChat) fetchUserStatus();

    if (!socket.connected) {
      socket.connect();
      socket.emit("setup", user);
    }

    socket.off("connected").on("connected", () => setSocketConnected(true));

    socket.off("typing").on("typing", ({ chatId, username }) => {
      if (selectedChat._id === chatId) {
        setTypingUsers((prev) => Array.from(new Set([...prev, username])));
      }
    });

    socket.off("stop typing").on("stop typing", ({ chatId, username }) => {
      if (selectedChat._id === chatId) {
        setTypingUsers((prev) => prev.filter((name) => name !== username));
      }
    });

    socket
      .off("message deleted")
      .on("message deleted", ({ messageId, chatId }) => {
        if (selectedChat._id === chatId) {
          setMessages((prev) => prev.filter((msg) => msg._id !== messageId));
        }
      });

    socket.off("user online").on("user online", (userId) => {
      setOnlineUsers((prev) =>
        prev.includes(userId) ? prev : [...prev, userId]
      );
    });

    socket.off("user offline").on("user offline", (userId) => {
      setOnlineUsers((prev) => prev.filter((id) => id !== userId));
    });

    socket.emit("get online users");
    socket.off("online users").on("online users", (ids) => {
      setOnlineUsers(ids);
    });

    return () => {
      socket.off("typing");
      socket.off("stop typing");
      socket.off("message deleted");
      socket.off("user online");
      socket.off("user offline");
      socket.off("online users");
    };
  }, [user, selectedChat, setFetchAgain, fetchUserStatus, setMessages]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages();
      socket.emit("join chat", selectedChat._id);
      localStorage.setItem("selectedChat", JSON.stringify(selectedChat));
    }
  }, [selectedChat, fetchMessages]);

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return "Offline";
    const lastSeenDate = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - lastSeenDate) / 60000);

    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hr ago`;

    return lastSeenDate.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const otherUser = selectedChat?.users
    ? getSenderFull(user, selectedChat.users)
    : null;

  return selectedChat ? (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        pb={3}
        w="100%"
      >
        <IconButton
          display={{ base: "flex", md: "none" }}
          icon={<ArrowBackIcon />}
          onClick={() => {
            setSelectedChat("");
            localStorage.removeItem("selectedChat");
          }}
        />
        {!selectedChat.isGroupChat ? (
          <>
            <Box display="flex" alignItems="center" gap={3}>
              <Box position="relative">
                <Avatar
                  size="md"
                  name={getSender(user, selectedChat.users)}
                  src={otherUser?.pic}
                />
                <Box
                  position="absolute"
                  bottom="2px"
                  right="2px"
                  w="10px"
                  h="10px"
                  borderRadius="full"
                  bg={
                    onlineUsers.includes(otherUser?._id)
                      ? "green.400"
                      : "gray.400"
                  }
                  border="2px solid white"
                />
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="lg">
                  {getSender(user, selectedChat.users)}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {onlineUsers.includes(otherUser?._id)
                    ? "Online"
                    : userStatusInfo.status === "offline" &&
                      userStatusInfo.lastSeen
                    ? formatLastSeen(userStatusInfo.lastSeen)
                    : "Offline"}
                </Text>
              </Box>
            </Box>
            <ProfileModal user={otherUser} />
          </>
        ) : (
          <>
            <Text fontWeight="bold" fontSize="lg">
              {selectedChat.chatName.toUpperCase()}
            </Text>
            <UpdateGroupChatModal
              fetchMessages={fetchMessages}
              fetchAgain={fetchAgain}
              setFetchAgain={setFetchAgain}
            />
          </>
        )}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-end"
        p={3}
        bg="white"
        w="100%"
        h="100%"
        borderRadius="lg"
        overflowY="hidden"
      >
        <div className="messages" style={{ flex: 1, overflowY: "auto" }}>
          <ScrollableChat
            messages={messages}
            onDelete={handleDelete}
            onlineUsers={onlineUsers}
            activeChatUserId={otherUser?._id}
          />
          {typingUsers.length > 0 && (
            <Box ml={2} fontSize="sm" color="gray.500">
              {typingUsers.join(", ")} typing...
            </Box>
          )}
        </div>

        <FormControl isRequired mt={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              icon={<BsFillMicFill />}
              aria-label="Start Recording"
              borderRadius="full"
              colorScheme={isRecording ? "red" : "gray"}
              onClick={startSpeechToText}
            />

            <Textarea
              value={newMessage}
              onChange={typingHandler}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Type a message"
              variant="filled"
              resize="none"
              rows={1}
              minHeight="45px"
              borderRadius="full"
              px={4}
              py={2}
              bg="#E0E0E0"
            />

            <IconButton
              icon={<AiOutlineSend />}
              onClick={sendMessage}
              aria-label="Send"
              borderRadius="full"
              colorScheme="teal"
              isLoading={sending}
            />
          </Box>
        </FormControl>
      </Box>
    </>
  ) : (
    <Box display="flex" alignItems="center" justifyContent="center" h="100%">
      <Text fontSize="3xl" pb={3}>
        Click on a user to start chatting
      </Text>
    </Box>
  );
};

export default SingleChat;
